import waHttp from './wa-http';
import waRequest from './wa-request';

export const baseApi = '/mass-notification/v1';

function createDraftMessage({organizationId, filter, personId, alertId, sitesPersonFilter, personGroupPersonFilter}) {
  const url = `${baseApi}/messages/draft`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data: {
      organizationId,
      initiationContext: {
        personFilter: filter,
        sitesPersonFilter,
        personId,
        alertId,
        personGroupPersonFilter,
      },
    },
  });
}

function createDraftMessageOpen(data) {
  const url = `${baseApi}/messages/draft`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data});
}

function updateMessage({params: {formats, conferenceBridge, statusConfirmationOptions, survey}}) {
  const url = `${baseApi}/messages/draft`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'patch',
    data: {
      messageConfig: {
        formats,
        conferenceBridge,
        statusConfirmationOptions,
        survey,
      },
    },
  });
}

function updateRecipientStatus(messageId, recipientId, confirmedStatus, comments, params) {
  const url = `${baseApi}/messages/${messageId}/recipients/${recipientId}/status`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'patch',
    params,
    data: {
      confirmedStatus,
      comments,
    },
  });
}

function getRecipientDetails({pathParams: {messageId, recipientId}, params}) {
  const url = `${baseApi}/messages/${messageId}/recipients/${recipientId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function deactivateMessage(messageId) {
  const url = `${baseApi}/messages/${messageId}/deactivate`;

  return waRequest.requestAuthenticated({url: url, method: 'patch'});
}

function sendDraftMessage({data}) {
  const url = `${baseApi}/messages/draft/send`;

  return waRequest.requestAuthenticated({url: url, method: 'patch', data});
}

function sendMessage(message) {
  const url = `${baseApi}/messages/send`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data: message});
}

function addRecipient(personId) {
  const url = `${baseApi}/messages/draft/recipients`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data: {
      personId,
    },
  });
}

function deleteRecipient(personId) {
  const url = `${baseApi}/messages/draft/recipients/${personId}`;

  return waRequest.requestAuthenticated({url: url, method: 'delete'});
}

function getMessage(messageId) {
  const url = `${baseApi}/messages/${messageId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function getMessageDetails({pathParams: {messageId}}) {
  return getMessage(messageId);
}

function getMessageSummary({pathParams: {messageId}, params}) {
  const url = `${baseApi}/messages/${messageId}/summary`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

/**
 * @deprecated please use getCrisis24StandardMessageTemplates instead
 * @param locale
 * @returns {Promise}
 */
function getMessageTemplates(locale = 'en') {
  const url = `/templates/v1/templates?categories=COMMUNICATOR_STANDARD&language=${locale}`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function getCrisis24StandardMessageTemplates({pathParams: {locale = 'en'}}) {
  const url = `/templates/v1/templates?categories=COMMUNICATOR_STANDARD&language=${locale}`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function getMessageRecipients(messageId, page, size) {
  const url = `${baseApi}/messages/${messageId}/recipients`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      page,
      size,
    },
  });
}

function getRecipients({pathParams: messageId, params: {locale, page, size, sort, includeDetails}, signal}) {
  const url = `${baseApi}/messages/${messageId}/recipients`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      languageCode: locale,
      page,
      size,
      sort,
      includeDetails,
    },
    signal,
  });
}

function getSurveyQuestionResponses({pathParams: {messageId, questionNumber}, params: {page, size, sort}}) {
  const url = `${baseApi}/messages/${messageId}/surveys/questions/${questionNumber}/responses`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      page,
      size,
      sort,
    },
  });
}

function getListOfCommunications({params: urlParams, data: filter, signal}) {
  const url = `${baseApi}/messages/history`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data: filter,
    params: urlParams,
    signal,
  });
}

function getPersonMessages({pathParams: {organizationId, personId}, params, data = {}, signal}) {
  const url = `${baseApi}/organizations/${organizationId}/persons/${personId}/messages`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data,
    params,
    signal,
  });
}

function getCommunicationsByMessageType({
  params: {organizationId, correlationId, messageType, locale, page, size, sort},
  signal,
}) {
  const url = `${baseApi}/messages/history`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      messageType,
      organizationId,
      correlationId,
      languageCode: locale,
      page,
      size,
      sort,
    },
    signal,
  });
}

function getResponseOptions({pathParams}) {
  const baseURL = process.env.REACT_APP_GATEWAY_URL;
  const apiKey = process.env.REACT_APP_AUTH_API_KEY;
  const url = `${baseApi}/messages/deliveries/${pathParams.deliveryId}/response-options`;

  return waHttp.httpRequest({
    baseURL,
    url: url,
    method: 'get',
    headers: {
      'x-api-key': apiKey,
    },
  });
}

function submitResponse({pathParams: deliveryInstanceId, params: urlParams, data: filter, signal}) {
  const baseURL = process.env.REACT_APP_GATEWAY_URL;
  const apiKey = process.env.REACT_APP_AUTH_API_KEY;
  const url = `${baseApi}/messages/deliveries/${deliveryInstanceId}/responses`;

  return waHttp.httpRequest({
    baseURL,
    url: url,
    method: 'post',
    data: filter,
    params: urlParams,
    signal,
    headers: {
      'x-api-key': apiKey,
    },
  });
}

function initiateMessagesExport(configData) {
  const {
    pathParams: {locale, format, sort},
    data,
  } = configData;

  const url = `${baseApi}/messages/history/export`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      languageCode: locale,
      format,
      sort,
    },
    data,
  });
}

function getMessagesExportStatus({id, successCallback}) {
  const url = `${baseApi}/exports/${id}`;

  return waRequest.requestWithRetryUntilSuccess({url: url, method: 'get'}, successCallback);
}

function initiateMessagesDetailsExport(configData) {
  const {
    pathParams: {messageId, locale, format, sort},
    data,
  } = configData;

  const url = `${baseApi}/messages/${messageId}/details/export`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      languageCode: locale,
      format,
      sort,
    },
    data,
  });
}

function extendMessage({pathParams, data}) {
  const {messageId} = pathParams;
  const {extendBy} = data;

  const url = `${baseApi}/messages/${messageId}/extend`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'patch',
    data: {
      extendBy,
    },
  });
}

function resendMessage({pathParams}) {
  const {messageId} = pathParams;
  const url = `${baseApi}/messages/${messageId}/resend`;

  return waRequest.requestAuthenticated({url: url, method: 'patch'});
}

//Message Templates
function createMessageTemplate({pathParams: {organizationId}, data: messageTemplate}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data: messageTemplate});
}

//Custom Message Templates
function getCustomMessageTemplates({pathParams: {organizationId}, params: {templateName, sort, disabled, sendable}}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      templateName,
      sort,
      disabled,
      sendable,
    },
  });
}

// Custom Message Templates with pagination
function getCustomMessageTemplatesPaginated({
  pathParams: {organizationId},
  params: {templateName, sort, disabled, page, size, quickSearchText},
}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      templateName,
      sort,
      disabled,
      paginate: true,
      page,
      size,
      quickSearchText,
    },
  });
}

function getCustomMessageTemplate({pathParams: {organizationId, templateId}}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates/${templateId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function updateCustomMessageTemplate({pathParams: {organizationId, templateId}, data: messageTemplate}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates/${templateId}`;

  return waRequest.requestAuthenticated({url: url, method: 'put', data: messageTemplate});
}

function deleteCustomMessageTemplate({pathParams: {organizationId, templateId}}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates/${templateId}`;

  return waRequest.requestAuthenticated({url: url, method: 'delete'});
}

function toggleTemplateDisabledStatus({pathParams: {orgId, templateId}, params: {disabledStatus}}) {
  const url = `${baseApi}/organizations/${orgId}/messages/templates/${templateId}/toggle`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'patch',
    data: {
      disabled: disabledStatus,
    },
  });
}

function getStandardTemplatesConfig({pathParams: {organizationId}}) {
  const url = `${baseApi}/organizations/${organizationId}/configs`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function createMassNotificationConfig({pathParams: {organizationId}, data: templateConfigurations}) {
  const url = `${baseApi}/organizations/${organizationId}/configs`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data: templateConfigurations});
}

function updateMassNotificationConfig({pathParams: {organizationId}, data: templateConfigurations}) {
  const url = `${baseApi}/organizations/${organizationId}/configs`;

  return waRequest.requestAuthenticated({url: url, method: 'put', data: templateConfigurations});
}

function getUsedLanguage({data: {text}}) {
  const url = `${baseApi}/detect-language`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data: {text}});
}

function getMessageDashboard({pathParams: {organizationId}}) {
  const url = `${baseApi}/organizations/${organizationId}/message-dashboard`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function scheduleMessage({pathParams: {organizationId}, data}) {
  const url = `${baseApi}/organizations/${organizationId}/scheduled-messages`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data});
}

function updateScheduleMessage({pathParams: {organizationId, scheduledMessageId}, data}) {
  const url = `${baseApi}/organizations/${organizationId}/scheduled-messages/${scheduledMessageId}`;

  return waRequest.requestAuthenticated({url: url, method: 'put', data});
}

function getScheduledMessages({pathParams: {organizationId}, params, data, signal}) {
  const url = `${baseApi}/organizations/${organizationId}/scheduled-messages/searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params,
    data,
    signal,
  });
}

function getScheduledMessageById({pathParams: {organizationId, scheduledMessageId}, signal}) {
  const url = `${baseApi}/organizations/${organizationId}/scheduled-messages/${scheduledMessageId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get', signal});
}

function deleteScheduledMessages({pathParams: {organizationId}, data}) {
  const url = `${baseApi}/organizations/${organizationId}/scheduled-messages`;

  return waRequest.requestAuthenticated({url: url, method: 'delete', data});
}

function deleteMessagesTemplates({pathParams: {organizationId}, data}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/templates`;

  return waRequest.requestAuthenticated({url: url, method: 'delete', data});
}

function addAttachments({pathParams: {organizationId}, data, params = {}}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/attachments`;

  return waRequest.requestAuthenticated({url: url, method: 'post', params, data});
}

function getAttachments({pathParams: {organizationId}, params = {}}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/attachments`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function deleteAttachments({pathParams: {organizationId}, attachmentIds}) {
  const url = `${baseApi}/organizations/${organizationId}/messages/attachments`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'delete',
    params: {
      attachmentIds,
    },
  });
}

function emailMessagePreview({data}) {
  const url = `${baseApi}/messages/previews/email`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data});
}

function voiceMessagePreview({data}) {
  const url = `${baseApi}/messages/previews/tts`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data,
    responseType: 'arraybuffer', //Important to tell axios how to properly handle the binary data response
  });
}

function getTemplateGroups({pathParams: {organizationId}, params}) {
  const url = `${baseApi}/organizations/${organizationId}/sender-recipients/groups`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function getTemplateSavedSearches({pathParams: {organizationId}, params}) {
  const url = `${baseApi}/organizations/${organizationId}/sender-recipients/saved-searches`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function getSenderRecipientsPeople({pathParams: {organizationId}, params}) {
  const url = `${baseApi}/organizations/${organizationId}/sender-recipients/people`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function getProperties({data}) {
  const url = `${baseApi}/properties`;

  return waRequest.requestAuthenticated({url: url, method: 'get', data});
}

/**
 * Returns desktop alerts registration details of an organization if present
 *
 * @param {Object} config - The configuration object.
 * @param {Object} config.pathParams - The path parameters.
 * @param {string} config.pathParams.organizationId - The ID of the organization.
 * @returns {Promise} A promise that resolves with the response of the request.
 */
function getDesktopAlertsRegistrations(config) {
  const {
    pathParams: {organizationId},
  } = config;

  const url = `${baseApi}/organizations/${organizationId}/desktop-alerts/registrations`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
  });
}

/**
 * Register an organization for desktop alerts.
 *
 * @param {Object} config - The configuration object.
 * @param {Object} config.pathParams - The path parameters.
 * @param {string} config.pathParams.organizationId - The ID of the organization.
 * @param {Object} config.data - The data for the registration.
 * @param {string} config.data.clientAppId - The client application ID.
 * @param {string} config.data.daServerEndpoint - The desktop alert server endpoint.
 * @returns {Promise} A promise that resolves with the response of the request.
 */
function postDesktopAlertsRegistration(config) {
  const {
    pathParams: {organizationId},
    data,
  } = config;

  const url = `${baseApi}/organizations/${organizationId}/desktop-alerts/registrations`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data: {
      ...data,
      organizationId,
    },
  });
}

/**
 * Delete organization registration for desktop alerts.
 *
 * @param {Object} config - The configuration object.
 * @param {Object} config.pathParams - The path parameters.
 * @param {string} config.pathParams.organizationId - The ID of the organization.
 * @returns {Promise} A promise that resolves with the response of the request.
 */
function deleteDesktopAlertsRegistration(config) {
  const {
    pathParams: {organizationId},
  } = config;

  const url = `${baseApi}/organizations/${organizationId}/desktop-alerts/registrations`;

  return waRequest.requestAuthenticated({url: url, method: 'delete'});
}

function getCanSendDesktopAlerts({signal, pathParams: {organizationId}}) {
  const url = `${baseApi}/organizations/${organizationId}/desktop-alerts/can-send`;

  return waRequest.requestAuthenticated({url: url, method: 'get', signal});
}

export default {
  createDraftMessage,
  updateMessage,
  sendDraftMessage,
  sendMessage,
  addRecipient,
  deleteRecipient,
  getMessage,
  getMessageSummary,
  getMessageRecipients,
  getMessageTemplates,
  getCrisis24StandardMessageTemplates,
  getListOfCommunications,
  getPersonMessages,
  getCommunicationsByMessageType,
  getResponseOptions,
  submitResponse,
  getMessageDetails,
  getRecipients,
  getSurveyQuestionResponses,
  initiateMessagesExport,
  getMessagesExportStatus,
  updateRecipientStatus,
  initiateMessagesDetailsExport,
  deactivateMessage,
  getRecipientDetails,
  extendMessage,
  resendMessage,
  createMessageTemplate,
  getCustomMessageTemplates,
  getCustomMessageTemplate,
  updateCustomMessageTemplate,
  toggleTemplateDisabledStatus,
  deleteCustomMessageTemplate,
  getStandardTemplatesConfig,
  createMassNotificationConfig,
  updateMassNotificationConfig,
  getUsedLanguage,
  getMessageDashboard,
  scheduleMessage,
  getScheduledMessages,
  deleteScheduledMessages,
  getScheduledMessageById,
  updateScheduleMessage,
  createDraftMessageOpen,
  addAttachments,
  getAttachments,
  deleteAttachments,
  getCustomMessageTemplatesPaginated,
  deleteMessagesTemplates,
  emailMessagePreview,
  voiceMessagePreview,
  getTemplateGroups,
  getTemplateSavedSearches,
  getSenderRecipientsPeople,
  getProperties,
  getDesktopAlertsRegistrations,
  postDesktopAlertsRegistration,
  deleteDesktopAlertsRegistration,
  getCanSendDesktopAlerts,
};
